<script lang="ts">
  import * as Popover from '$lib/components/ui/popover/index.js';
  import * as AlertDialog from '$lib/components/ui/alert-dialog/index.js';
  import Button from '@/lib/components/ui/button/button.svelte';
  import { Calendar } from '$lib/components/ui/calendar/index.js';
  import {
    CalendarDate,
    DateFormatter,
    getLocalTimeZone,
    today as getToday,
    type DateValue,
  } from '@internationalized/date';
  import { getHistoricalMmpCurve, type HistoricalMmpDataResponse } from '@/Util/api';

  export let onLoad: (data: HistoricalMmpDataResponse) => void;
  export let onClear: () => void;
  export let hasData: boolean = false;
  export let maxDuration: number | null = null;

  const df = new DateFormatter('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const tz = getLocalTimeZone();
  const todayValue = getToday(tz);
  const currentYear = todayValue.year;

  let startDateValue: DateValue = new CalendarDate(currentYear, 1, 1);
  let endDateValue: DateValue = todayValue;
  let startPickerValue: DateValue | undefined = undefined;
  let endPickerValue: DateValue | undefined = undefined;
  let startOpen = false;
  let endOpen = false;

  let isLoading = false;
  let confirmOpen = false;
  let pendingMissingCount = 0;
  let pendingTotalCount = 0;
  let errorMsg = '';
  let activePreset: string | null = `y${currentYear}`;

  $: if (startPickerValue) {
    startDateValue = startPickerValue;
    startPickerValue = undefined;
    activePreset = null;
    startOpen = false;
  }

  $: if (endPickerValue) {
    endDateValue = endPickerValue;
    endPickerValue = undefined;
    activePreset = null;
    endOpen = false;
  }

  $: startDate = startDateValue.toString();
  $: endDate = endDateValue.toString();

  const presets = [
    { id: '7d',   label: '7D',              apply: () => ({ start: todayValue.subtract({ days: 7 }),    end: todayValue }) },
    { id: '1m',   label: '1M',              apply: () => ({ start: todayValue.subtract({ months: 1 }), end: todayValue }) },
    { id: '3m',   label: '3M',              apply: () => ({ start: todayValue.subtract({ months: 3 }), end: todayValue }) },
    { id: '1y',   label: '1Y',              apply: () => ({ start: todayValue.subtract({ years: 1 }),  end: todayValue }) },
    { id: `y${currentYear - 2}`, label: String(currentYear - 2), apply: () => ({ start: new CalendarDate(currentYear - 2, 1, 1), end: new CalendarDate(currentYear - 2, 12, 31) }) },
    { id: `y${currentYear - 1}`, label: String(currentYear - 1), apply: () => ({ start: new CalendarDate(currentYear - 1, 1, 1), end: new CalendarDate(currentYear - 1, 12, 31) }) },
    { id: `y${currentYear}`,     label: `${currentYear} YTD`,    apply: () => ({ start: new CalendarDate(currentYear, 1, 1), end: todayValue }) },
  ];

  function applyPreset(id: string) {
    const preset = presets.find(p => p.id === id);
    if (!preset) return;
    const { start, end } = preset.apply();
    startDateValue = start;
    endDateValue = end;
    activePreset = id;
  }

  async function handleLoad() {
    if (!startDate || !endDate) return;
    errorMsg = '';
    isLoading = true;
    try {
      const result = await getHistoricalMmpCurve(startDate, endDate, false, maxDuration);
      if (result.needs_confirm) {
        pendingMissingCount = result.missing_count;
        pendingTotalCount = result.total_count;
        confirmOpen = true;
      } else {
        onLoad(result);
      }
    } catch (e) {
      errorMsg = 'Failed to load. Please try again. Error: ' + (e instanceof Error ? e.message : String(e));
    } finally {
      isLoading = false;
    }
  }

  async function handleConfirm() {
    confirmOpen = false;
    errorMsg = '';
    isLoading = true;
    try {
      const result = await getHistoricalMmpCurve(startDate, endDate, true, maxDuration);
      if (!result.needs_confirm) onLoad(result);
    } catch {
      errorMsg = 'Failed to load. Please try again.';
    } finally {
      isLoading = false;
    }
  }

  function handleClear() {
    onClear();
  }
</script>

<div class="flex flex-wrap items-center gap-2">
  <!-- Section label -->
  <span class="flex select-none items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
    <i class="fa-regular fa-clock-rotate-left" />
    Historical
  </span>

  <!-- Preset range chips -->
  <div class="flex items-center gap-px rounded-md border border-border bg-background p-0.5">
    {#each presets as preset}
      <button
        class="rounded px-2 py-[3px] text-[11px] font-semibold tracking-wide transition-colors
          {activePreset === preset.id
            ? 'bg-amber-500/20 text-amber-400 shadow-sm'
            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}"
        on:click={() => applyPreset(preset.id)}
      >
        {preset.label}
      </button>
    {/each}
  </div>

  <!-- Date range (always visible, each date is a calendar trigger) -->
  <div class="flex items-center rounded-md border transition-colors {hasData ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-background'}">
    <Popover.Root bind:open={startOpen} portal="#shadcn-ui-dialog-target">
      <Popover.Trigger asChild let:builder>
        <button
          use:builder.action {...builder}
          class="rounded-l-md px-2.5 py-[5px] text-[11px] tabular-nums font-medium text-foreground transition-colors hover:bg-muted/40"
        >
          {df.format(startDateValue.toDate(tz))}
        </button>
      </Popover.Trigger>
      <Popover.Content class="w-auto p-0" align="start">
        <Calendar bind:value={startPickerValue} placeholder={startDateValue} maxValue={endDateValue} initialFocus />
      </Popover.Content>
    </Popover.Root>

    <span class="select-none text-xs text-muted-foreground/50">–</span>

    <Popover.Root bind:open={endOpen} portal="#shadcn-ui-dialog-target">
      <Popover.Trigger asChild let:builder>
        <button
          use:builder.action {...builder}
          class="rounded-r-md px-2.5 py-[5px] text-[11px] tabular-nums font-medium text-foreground transition-colors hover:bg-muted/40"
        >
          {df.format(endDateValue.toDate(tz))}
        </button>
      </Popover.Trigger>
      <Popover.Content class="w-auto p-0" align="start">
        <Calendar bind:value={endPickerValue} placeholder={endDateValue} minValue={startDateValue} maxValue={todayValue} initialFocus />
      </Popover.Content>
    </Popover.Root>
  </div>

  <!-- Actions -->
  <div class="flex items-center gap-1">
    {#if hasData}
      <button
        class="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        title="Clear historical data"
        on:click={handleClear}
      >
        <i class="fa-regular fa-xmark text-xs" />
      </button>
    {/if}

    <Button
      size="sm"
      class="h-7 gap-1.5 text-xs"
      variant={hasData ? 'outline' : 'default'}
      disabled={isLoading}
      on:click={handleLoad}
    >
      {#if isLoading}
        <i class="fa-regular fa-spinner animate-spin" />
        Loading…
      {:else if hasData}
        <i class="fa-regular fa-arrows-rotate text-xs" />
        Reload
      {:else}
        <i class="fa-regular fa-chart-line text-xs" />
        Load
      {/if}
    </Button>
  </div>

  {#if errorMsg}
    <span class="text-[11px] text-destructive">{errorMsg}</span>
  {/if}
</div>

<AlertDialog.Root bind:open={confirmOpen} portal="#shadcn-ui-dialog-target">
  <AlertDialog.Content>
    <AlertDialog.Header>
      <AlertDialog.Title>Some activities are missing MMP data</AlertDialog.Title>
      <AlertDialog.Description>
        {pendingMissingCount} of {pendingTotalCount}
        {pendingTotalCount === 1 ? 'activity' : 'activities'} in this range
        {pendingMissingCount === 1 ? "hasn't" : "haven't"} had their power curve computed yet.
        Computing them now may take a moment.
      </AlertDialog.Description>
    </AlertDialog.Header>
    <AlertDialog.Footer>
      <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
      <AlertDialog.Action asChild let:builder>
        <Button builders={[builder]} on:click={handleConfirm}>Proceed</Button>
      </AlertDialog.Action>
    </AlertDialog.Footer>
  </AlertDialog.Content>
</AlertDialog.Root>
